/**
 * Mock AI copilot responses for Track 4.
 *
 * The copilot returns a `reply` (markdown for the chat bubble) and an optional
 * `intent` that the viewer executes — see useCopilotIntents.ts (Track 4).
 */
export type CopilotIntent =
  | { type: "select"; elementIds: number[] }
  | { type: "isolate"; elementIds: number[] }
  | { type: "filterByClass"; ifcClass: string }
  | { type: "flyTo"; position: [number, number, number] }
  | { type: "highlightClashes"; severity?: "critical" | "major" | "minor" }
  | { type: "none" };

export interface MockCopilotResponse {
  reply: string;
  intent: CopilotIntent;
  /** Optional follow-up suggestions to display as chips below the reply. */
  suggestions?: string[];
}

interface Rule {
  match: RegExp;
  respond: () => MockCopilotResponse;
}

const rules: Rule[] = [
  {
    match: /\b(chw|chilled water|200\s*mm).*\b(pipe|run)\b/i,
    respond: () => ({
      reply:
        "Found **3 chilled-water pipes** at 200 mm on Level 5. Isolating them now. " +
        "Two of them clash with structural beams on the same level — want me to open those clashes?",
      intent: { type: "isolate", elementIds: [100036, 100037, 100038] },
      suggestions: ["Show those clashes", "Hide everything else", "Export this view"],
    }),
  },
  {
    match: /\b(critical|red).*\b(clash|conflict)/i,
    respond: () => ({
      reply:
        "There are **4 critical clashes** open in this project. The worst one is between RC Column C-014 and a 600×400 supply duct on Level 5 (142 mm overlap).",
      intent: { type: "highlightClashes", severity: "critical" },
      suggestions: ["Open clash workbench", "Filter to MEP only", "Assign all to me"],
    }),
  },
  {
    match: /\b(column|c-\d+|rc column)\b/i,
    respond: () => ({
      reply:
        "Isolating all RC columns. Found **478** across the building, predominantly 600×600 C40 with rebar configurations per the structural notes.",
      intent: { type: "filterByClass", ifcClass: "IfcColumn" },
      suggestions: ["Group by level", "Show only load-bearing", "Export schedule"],
    }),
  },
  {
    match: /\b(beam|primary beam|b-\d+)\b/i,
    respond: () => ({
      reply: "Isolating all primary beams (UB 533×210). 612 elements found.",
      intent: { type: "filterByClass", ifcClass: "IfcBeam" },
      suggestions: ["Show secondary beams too", "Highlight clashes", "Group by zone"],
    }),
  },
  {
    match: /\b(wall|exterior|external|curtain)\b/i,
    respond: () => ({
      reply:
        "Filtering to **IfcWall**. 1,844 walls in total — 412 are external (load-bearing), the rest are internal partitions.",
      intent: { type: "filterByClass", ifcClass: "IfcWall" },
      suggestions: ["External only", "Show fire ratings", "Compare to v2"],
    }),
  },
  {
    match: /\b(level|floor)\s*(\d+)\b/i,
    respond: () => ({
      reply: "Flying to that level and applying a section box at the slab below.",
      intent: { type: "flyTo", position: [40, 18, 22] },
      suggestions: ["Show clashes here", "Filter to MEP", "Save this view"],
    }),
  },
  {
    match: /\b(help|what can|how)\b/i,
    respond: () => ({
      reply:
        "I can help you **find elements** (\"show all 200 mm CHW pipes\"), " +
        "**investigate clashes** (\"open the critical clashes on Level 5\"), " +
        "**filter by class or discipline**, and **save views**. " +
        "Try asking in plain language.",
      intent: { type: "none" },
      suggestions: [
        "Show all critical clashes",
        "Isolate columns on Level 5",
        "Find walls with EI120 fire rating",
      ],
    }),
  },
];

export function mockCopilotResponse(question: string): MockCopilotResponse {
  for (const rule of rules) {
    if (rule.match.test(question)) return rule.respond();
  }
  return {
    reply:
      "I couldn't map that to a viewer action yet — try a phrase like \"show all 200 mm pipes on Level 5\" or \"open the critical clashes\".",
    intent: { type: "none" },
    suggestions: [
      "Show all critical clashes",
      "Isolate columns on Level 5",
      "Find walls with EI120 fire rating",
    ],
  };
}

export const mockCopilotSuggestedPrompts: string[] = [
  "Show all 200 mm CHW pipes on Level 5",
  "Open the critical clashes on Level 5",
  "Isolate RC columns",
  "Walls with EI120 fire rating",
  "Compare against v2 of this model",
];
